// Lógica de negocio del customizer de plantillas (TemplateReportDetail), separada de la vista.
// Funciones puras / de servicio (sin Vue ni `this`): construir páginas, aplanar grupos,
// auto-enlace disciplina↔grupo, validación de disciplinas, subir portada y persistir.
import { discName, TYPES } from '../../report-page-builder/data/page-builder.render'
import { templatePages } from '../../report-page-builder/data/report-templates.data'
import { matchGroupForDiscipline } from '../../report-page-builder/services/page-builder.converge'
import { saveBuilderConfig } from '../../report-page-builder/services/page-builder.service'
import { uploadFile } from '·/components/activity-groups/utils/uppyFactory'

// Páginas editables (id/visible/orden/settings): las de la plantilla si trae `pages`
// (config traída de otro proyecto) o las de la plantilla canónica por `key`.
export function buildCustomizerPages(template, templateKey) {
  const src = (template && Array.isArray(template.pages) && template.pages.length)
    ? template.pages
    : (templatePages(templateKey) || [])
  return src.map((p, i) => ({
    id: i + 1,
    type: p.type,
    visible: p.visible !== false,
    settings: Object.assign({}, p.settings),
  }))
}

// Árbol de grupos aplanado para el auto-enlace: [{ id, name, code_custom }].
export function flattenGroups(activityGroups) {
  const out = []
  const walk = (arr) => (arr || []).forEach((n) => {
    out.push({ id: n.id, name: n.label || n.name, code_custom: n.code_custom || null })
    if (n.children) walk(n.children)
  })
  walk(activityGroups || [])
  return out
}

// Copia de páginas con auto-enlace disciplina↔grupo (code_custom + heurística).
// No pisa groupId existentes; respeta unicidad (un grupo por disciplina).
export function autoLinkPages(pages, flatGroups) {
  const out = (pages || []).map((p) => ({ type: p.type, visible: p.visible !== false, settings: Object.assign({}, p.settings) }))
  if (!flatGroups || !flatGroups.length) return out
  const disc = out.filter((p) => p.type === 'discipline')
  const used = []
  disc.forEach((p) => { if (p.settings.groupId != null) used.push(p.settings.groupId) })
  const run = (mode) => disc.forEach((p) => {
    if (p.settings.groupId != null) return
    const m = matchGroupForDiscipline(p.settings.discipline, flatGroups, used, mode)
    if (m) { p.settings.groupId = m.groupId; used.push(m.groupId) }
  })
  run('exact'); run('heuristic')
  return out
}

// ¿La fila admite configuración inline? (portada; disciplina renombrable; dashboard custom)
export function rowNeedsConfig(page) {
  if (!page || page.visible === false) return false
  if (page.type === 'cover') return true
  if (page.type === 'discipline') return true
  if (page.type === 'dashboard') return page.settings && page.settings.showGroupProgress === 'custom'
  return false
}

// Nombre visible de una página (la disciplina muestra su nombre de sección).
export function pageDisplayName(page) {
  if (page.type === 'discipline') return (page.settings && page.settings.name) || discName(page.settings && page.settings.discipline)
  return TYPES[page.type] ? TYPES[page.type].name : page.type
}

// Disciplinas VISIBLES que no quedaron enlazadas a un grupo (bloquean aplicar).
// Recibe páginas YA auto-enlazadas. Devuelve nombres/códigos.
export function missingDisciplines(linkedPages) {
  return (linkedPages || [])
    .filter((p) => p.type === 'discipline' && p.visible !== false && p.settings.groupId == null)
    .map((p) => p.settings.name || p.settings.discipline)
}

// Sube a S3 (uppy) la imagen de portada pendiente (File) y deja la key en image_cover.url.
// Muta el objeto coverPage pasado. Devuelve true si subió algo.
export async function uploadCoverImage(coverPage) {
  const ic = coverPage && coverPage.settings && coverPage.settings.image_cover
  if (!ic || !ic.file) return false
  const { file, response } = await uploadFile(ic.file)
  ic.type = 'file'; ic.name = file.name; ic.mimeType = file.type; ic.url = response.body.key; ic.file = null
  return true
}

// Persiste la config del customizer en reports.detailed (delega en el servicio del builder).
export function persistCustomizerConfig({ projectId, pages, templateKey, importedFrom }) {
  return saveBuilderConfig(projectId, { version: 1, pages, templateKey, importedFrom })
}
